import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import {
  STATUS_LIST,
  STATUS_LABEL,
  KAWIN_LABEL,
  KELAMIN_LABEL,
  DISABILITAS_LABEL,
} from "@/lib/constants";
import { Search, Plus, Eye, Pencil, Trash2, X, ShieldCheck, MapPin, Download } from "lucide-react";
import { toast } from "sonner";

export default function VotersPage() {
  const { user } = useAuth();
  const canEdit = ["admin", "panitia"].includes(user?.role);
  const canDelete = user?.role === "admin";
  const canPropose = user?.role === "ppdp";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [noKkFilter, setNoKkFilter] = useState("");
  const [alamatFilter, setAlamatFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dusunFilter, setDusunFilter] = useState("all");
  const [rwFilter, setRwFilter] = useState("all");
  const [rtFilter, setRtFilter] = useState("all");
  const [tpsFilter, setTpsFilter] = useState("all");
  const [dusuns, setDusuns] = useState([]);
  const [tpsList, setTpsList] = useState([]);
  const [rwOpts, setRwOpts] = useState([]);
  const [rtOpts, setRtOpts] = useState([]);
  const [exportCount, setExportCount] = useState(null); // preview count untuk tombol Unduh Excel
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openDetail, setOpenDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q) params.q = q;
      if (noKkFilter.trim()) params.no_kk = noKkFilter.trim();
      if (alamatFilter.trim()) params.alamat = alamatFilter.trim();
      if (statusFilter !== "all") {
        params.status = statusFilter === "ACTIVE_PLUS_PINDAH" ? "ACTIVE,PINDAH_DOMISILI" : statusFilter;
      }
      if (dusunFilter !== "all") params.dusun = dusunFilter;
      if (rwFilter !== "all") params.rw = rwFilter;
      if (rtFilter !== "all") params.rt = rtFilter;
      if (tpsFilter !== "all") params.tps = tpsFilter;
      const { data } = await api.get("/voters", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/dusuns").then(({ data }) => setDusuns(data || [])).catch(() => {});
    api.get("/tps").then(({ data }) => setTpsList(data || [])).catch(() => {});
  }, []);

  // Preview count untuk tombol Unduh Excel — sinkron dengan filter aktif
  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (noKkFilter.trim()) params.no_kk = noKkFilter.trim();
    if (alamatFilter.trim()) params.alamat = alamatFilter.trim();
    if (statusFilter !== "all") {
      params.status = statusFilter === "ACTIVE_PLUS_PINDAH" ? "ACTIVE,PINDAH_DOMISILI" : statusFilter;
    }
    if (dusunFilter !== "all") params.dusun = dusunFilter;
    if (rwFilter !== "all") params.rw = rwFilter;
    if (rtFilter !== "all") params.rt = rtFilter;
    if (tpsFilter !== "all") params.tps = tpsFilter;
    api.get("/exports/voters-all/count", { params })
      .then(({ data }) => setExportCount(data?.total ?? null))
      .catch(() => setExportCount(null));
  }, [q, noKkFilter, alamatFilter, statusFilter, dusunFilter, rwFilter, rtFilter, tpsFilter]);

  // Load RW options when dusun changes. For PPDP, RW options come from their TPS's voters.
  useEffect(() => {
    if (user?.role === "ppdp" && user.assigned_tps) {
      // Fetch wilayah info for assigned TPS to know which RWs exist
      api.get(`/tps/${user.assigned_tps}/wilayah`)
        .then(({ data }) => {
          const rws = (data.rw_breakdown || []).map((r) => r.rw);
          setRwOpts(rws);
        })
        .catch(() => setRwOpts([]));
      return;
    }
    if (dusunFilter === "all") { setRwOpts([]); setRwFilter("all"); setRtFilter("all"); return; }
    api.get("/wilayah/options", { params: { dusun: dusunFilter } })
      .then(({ data }) => setRwOpts(data.rws || []))
      .catch(() => setRwOpts([]));
    setRwFilter("all"); setRtFilter("all");
  }, [dusunFilter, user?.role, user?.assigned_tps]);

  // Load RT options when rw changes. For PPDP, RT list is derived from wilayah endpoint.
  useEffect(() => {
    if (user?.role === "ppdp" && user.assigned_tps && rwFilter !== "all") {
      api.get(`/tps/${user.assigned_tps}/wilayah`)
        .then(({ data }) => {
          const rw = (data.rw_breakdown || []).find((r) => r.rw === rwFilter);
          setRtOpts((rw?.rts || []).map((r) => r.rt));
        })
        .catch(() => setRtOpts([]));
      return;
    }
    if (dusunFilter === "all" || rwFilter === "all") { setRtOpts([]); setRtFilter("all"); return; }
    api.get("/wilayah/options", { params: { dusun: dusunFilter, rw: rwFilter } })
      .then(({ data }) => setRtOpts(data.rts || []))
      .catch(() => setRtOpts([]));
    setRtFilter("all");
  }, [dusunFilter, rwFilter, user?.role, user?.assigned_tps]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, noKkFilter, alamatFilter, statusFilter, dusunFilter, rwFilter, rtFilter, tpsFilter]);

  const handleSave = async (payload) => {
    try {
      if (editing) {
        await api.put(`/voters/${editing.id}`, payload);
        toast.success("Pemilih diperbarui.");
      } else {
        await api.post("/voters", payload);
        toast.success("Pemilih ditambahkan.");
      }
      setOpenForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (voter) => {
    if (!window.confirm(`Hapus pemilih "${voter.nama}" (NIK ${voter.nik})?\n\nData akan disembunyikan dari DPT (soft delete) dan masih tersimpan di audit log.`)) return;
    try {
      await api.delete(`/voters/${voter.id}`);
      toast.success("Pemilih dihapus.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="voters-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Data Pemilih</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
            Daftar Pemilih
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            Total <span className="font-bold text-slate-900">{total.toLocaleString("id-ID")}</span> pemilih
            {dusunFilter !== "all" && ` di ${dusunFilter}`}.
          </p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const params = {};
                  if (q) params.q = q;
                  if (noKkFilter.trim()) params.no_kk = noKkFilter.trim();
                  if (alamatFilter.trim()) params.alamat = alamatFilter.trim();
                  if (statusFilter !== "all") {
        params.status = statusFilter === "ACTIVE_PLUS_PINDAH" ? "ACTIVE,PINDAH_DOMISILI" : statusFilter;
      }
                  if (dusunFilter !== "all") params.dusun = dusunFilter;
                  if (rwFilter !== "all") params.rw = rwFilter;
                  if (rtFilter !== "all") params.rt = rtFilter;
                  if (tpsFilter !== "all") params.tps = tpsFilter;
                  const res = await api.get("/exports/voters-all", { params, responseType: "blob" });
                  const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  // pakai filename dari Content-Disposition header kalau ada
                  const cd = res.headers?.["content-disposition"] || "";
                  const m = /filename="([^"]+)"/.exec(cd);
                  a.download = m ? m[1] : `Data_Pemilih_${new Date().toISOString().replace(/[-:T]/g, "").slice(0, 12)}.xlsx`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                  const hasFilter = Object.keys(params).length > 0;
                  toast.success(hasFilter ? "Excel diunduh dengan filter yang aktif." : "Semua data pemilih diunduh.");
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="h-11"
              data-testid="download-voters-xlsx"
            >
              <Download className="h-4 w-4 mr-2" />
              {exportCount != null
                ? `Unduh Excel (${exportCount.toLocaleString("id-ID")})`
                : "Unduh Excel"}
            </Button>
            <Button
              onClick={() => { setEditing(null); setOpenForm(true); }}
              className="bg-slate-900 hover:bg-slate-800 h-11"
              data-testid="add-voter-button"
            >
              <Plus className="h-4 w-4 mr-2" /> Tambah Pemilih
            </Button>
          </div>
        )}
      </header>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-5 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama, NIK, atau No. KK"
            className="pl-9 h-11"
            data-testid="voters-search-input"
          />
        </div>
        <div className="md:col-span-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-11" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="ACTIVE_PLUS_PINDAH" data-testid="filter-status-active-plus-pindah">Aktif + Pindah Domisili</SelectItem>
              {STATUS_LIST.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {user?.role !== "ppdp" && (
          <div className="md:col-span-4">
            <Select value={tpsFilter} onValueChange={setTpsFilter}>
              <SelectTrigger className="h-11" data-testid="filter-tps"><SelectValue placeholder="TPS" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua TPS</SelectItem>
                {tpsList.map((t) => <SelectItem key={t.id} value={t.kode}>{t.kode}{t.nama ? ` — ${t.nama}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {user?.role !== "ppdp" && (
          <div className="md:col-span-4">
            <Select value={dusunFilter} onValueChange={setDusunFilter}>
              <SelectTrigger className="h-11" data-testid="filter-dusun"><SelectValue placeholder="Dusun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Dusun</SelectItem>
                {dusuns.map((d) => <SelectItem key={d.id} value={d.nama}>{d.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="md:col-span-4">
          <Select value={rwFilter} onValueChange={setRwFilter} disabled={user?.role !== "ppdp" && dusunFilter === "all"}>
            <SelectTrigger className="h-11" data-testid="filter-rw"><SelectValue placeholder="RW" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua RW</SelectItem>
              {rwOpts.map((rw) => <SelectItem key={rw} value={rw}>RW {rw}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-4">
          <Select value={rtFilter} onValueChange={setRtFilter} disabled={rwFilter === "all"}>
            <SelectTrigger className="h-11" data-testid="filter-rt"><SelectValue placeholder="RT" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua RT</SelectItem>
              {rtOpts.map((rt) => <SelectItem key={rt} value={rt}>RT {rt}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-6">
          <Input
            value={noKkFilter}
            onChange={(e) => setNoKkFilter(e.target.value)}
            placeholder="Filter No. KK (cocokkan keluarga sama)"
            className="h-11 font-mono"
            data-testid="filter-no-kk"
          />
        </div>
        <div className="md:col-span-6">
          <Input
            value={alamatFilter}
            onChange={(e) => setAlamatFilter(e.target.value)}
            placeholder="Filter Alamat (cocokkan rumah sama)"
            className="h-11"
            data-testid="filter-alamat"
          />
        </div>
      </div>

      {/* Mobile cards */}
      <div className="grid grid-cols-1 md:hidden gap-3">
        {loading && <div className="text-sm text-slate-500">Memuat...</div>}
        {!loading && items.length === 0 && <div className="text-sm text-slate-500">Tidak ada data.</div>}
        {items.map((v) => (
          <VoterCard
            key={v.id}
            voter={v}
            onView={() => setOpenDetail(v)}
            onEdit={canEdit ? () => { setEditing(v); setOpenForm(true); } : null}
            onDelete={canDelete ? () => handleDelete(v) : null}
          />
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block rounded-md border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Nama</th>
                <th className="text-left px-4 py-3 font-semibold">NIK</th>
                <th className="text-left px-4 py-3 font-semibold">JK</th>
                <th className="text-left px-4 py-3 font-semibold">Alamat</th>
                <th className="text-left px-4 py-3 font-semibold">RT/RW</th>
                <th className="text-left px-4 py-3 font-semibold">Dusun</th>
                <th className="text-left px-4 py-3 font-semibold">TPS</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">Memuat...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">Tidak ada data.</td></tr>
              )}
              {items.map((v) => (
                <tr key={v.id} className="border-t border-slate-200 hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold">{v.nama}</td>
                  <td className="px-4 py-3 font-mono text-xs">{v.nik}</td>
                  <td className="px-4 py-3">{v.jenis_kelamin}</td>
                  <td className="px-4 py-3 text-slate-600">{v.alamat || "-"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{v.rt || "-"}/{v.rw || "-"}</td>
                  <td className="px-4 py-3">{v.dusun || "-"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{v.tps || "-"}</td>
                  <td className="px-4 py-3"><StatusBadge status={v.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setOpenDetail(v)} data-testid={`view-voter-${v.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => { setEditing(v); setOpenForm(true); }} data-testid={`edit-voter-${v.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(v)} className="text-red-700 hover:bg-red-50" data-testid={`delete-voter-${v.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* PPDP hint */}
      {canPropose && (
        <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 p-4 text-sm flex gap-3">
          <ShieldCheck className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div>
            Sebagai PPDP, perubahan data dilakukan melalui menu <strong>Coklit</strong> untuk diverifikasi Panitia.
          </div>
        </div>
      )}

      {/* Form Dialog */}
      <VoterFormDialog
        open={openForm}
        onClose={() => { setOpenForm(false); setEditing(null); }}
        onSave={handleSave}
        voter={editing}
        dusuns={dusuns}
        tpsList={tpsList}
      />

      {/* Detail Dialog */}
      <VoterDetailDialog voter={openDetail} onClose={() => setOpenDetail(null)} />
    </div>
  );
}

function VoterCard({ voter, onView, onEdit, onDelete }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-bold text-base truncate">{voter.nama}</div>
          <div className="text-xs font-mono text-slate-500 mt-0.5">{voter.nik}</div>
        </div>
        <StatusBadge status={voter.status} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div><span className="text-slate-500">JK:</span> {KELAMIN_LABEL[voter.jenis_kelamin] || voter.jenis_kelamin}</div>
        <div className="font-mono"><span className="text-slate-500 font-sans">RT/RW:</span> {voter.rt || "-"}/{voter.rw || "-"}</div>
        <div className="col-span-2 flex items-center gap-1 text-slate-600">
          <MapPin className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{voter.alamat || "-"}, {voter.dusun || "-"}</span>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="outline" onClick={onView} className="flex-1">
          <Eye className="h-4 w-4 mr-1" /> Detail
        </Button>
        {onEdit && (
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
        )}
        {onDelete && (
          <Button size="sm" variant="outline" onClick={onDelete} className="text-red-700 hover:bg-red-50 border-red-200">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function VoterDetailDialog({ voter, onClose }) {
  return (
    <Dialog open={!!voter} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{voter?.nama}</DialogTitle>
        </DialogHeader>
        {voter && (
          <div className="space-y-3 text-sm">
            <StatusBadge status={voter.status} />
            <Row label="NIK" value={<span className="font-mono">{voter.nik}</span>} />
            <Row label="No. KK" value={<span className="font-mono">{voter.no_kk || "-"}</span>} />
            <Row label="Tempat / Tanggal Lahir" value={`${voter.tempat_lahir || "-"} / ${voter.tanggal_lahir || "-"}`} />
            <Row label="Jenis Kelamin" value={KELAMIN_LABEL[voter.jenis_kelamin] || voter.jenis_kelamin} />
            <Row label="Status Kawin" value={KAWIN_LABEL[voter.status_perkawinan] || voter.status_perkawinan || "-"} />
            <Row label="Alamat" value={voter.alamat || "-"} />
            <Row label="RT / RW" value={`${voter.rt || "-"} / ${voter.rw || "-"}`} />
            <Row label="Dusun" value={voter.dusun || "-"} />
            <Row label="TPS" value={voter.tps || "-"} />
            <Row label="Disabilitas" value={DISABILITAS_LABEL[voter.disabilitas] || "-"} />
            {voter.keterangan && <Row label="Keterangan" value={voter.keterangan} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-2 border-b border-slate-100 pb-2">
      <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className="col-span-2 text-slate-800">{value}</div>
    </div>
  );
}

function VoterFormDialog({ open, onClose, onSave, voter, dusuns, tpsList }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm(voter ? { ...voter } : {
      nik: "", nama: "", jenis_kelamin: "L", status: "ACTIVE", disabilitas: 0,
    });
  }, [voter, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nik || form.nik.length !== 16) {
      toast.error("NIK harus 16 digit");
      return;
    }
    if (!form.nama) { toast.error("Nama wajib"); return; }
    if (!form.jenis_kelamin) { toast.error("Jenis kelamin wajib"); return; }
    onSave({ ...form, disabilitas: Number(form.disabilitas) || 0 });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {voter ? "Ubah Pemilih" : "Tambah Pemilih"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="voter-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="NIK *" required><Input maxLength={16} value={form.nik || ""} onChange={(e) => update("nik", e.target.value.replace(/\D/g, ""))} disabled={!!voter} className="font-mono h-11" data-testid="form-nik" /></Field>
            <Field label="No. KK"><Input maxLength={16} value={form.no_kk || ""} onChange={(e) => update("no_kk", e.target.value.replace(/\D/g, ""))} className="font-mono h-11" /></Field>
            <Field label="Nama *" required className="md:col-span-2"><Input value={form.nama || ""} onChange={(e) => update("nama", e.target.value.toUpperCase())} className="h-11" data-testid="form-nama" /></Field>
            <Field label="Tempat Lahir"><Input value={form.tempat_lahir || ""} onChange={(e) => update("tempat_lahir", e.target.value)} className="h-11" /></Field>
            <Field label="Tanggal Lahir"><Input type="date" value={form.tanggal_lahir || ""} onChange={(e) => update("tanggal_lahir", e.target.value)} className="h-11" /></Field>
            <Field label="Jenis Kelamin *" required>
              <Select value={form.jenis_kelamin} onValueChange={(v) => update("jenis_kelamin", v)}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="L">Laki-laki</SelectItem>
                  <SelectItem value="P">Perempuan</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status Kawin">
              <Select value={form.status_perkawinan || ""} onValueChange={(v) => update("status_perkawinan", v)}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="B">Belum Kawin</SelectItem>
                  <SelectItem value="S">Kawin</SelectItem>
                  <SelectItem value="P">Pernah Kawin</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Alamat" className="md:col-span-2"><Input value={form.alamat || ""} onChange={(e) => update("alamat", e.target.value)} className="h-11" /></Field>
            <Field label="RT"><Input value={form.rt || ""} onChange={(e) => update("rt", e.target.value)} className="font-mono h-11" /></Field>
            <Field label="RW"><Input value={form.rw || ""} onChange={(e) => update("rw", e.target.value)} className="font-mono h-11" /></Field>
            <Field label="Dusun">
              <Select value={form.dusun || ""} onValueChange={(v) => update("dusun", v)}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Pilih dusun" /></SelectTrigger>
                <SelectContent>
                  {dusuns.map((d) => <SelectItem key={d.id} value={d.nama}>{d.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="TPS">
              <Select value={form.tps || ""} onValueChange={(v) => update("tps", v)}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Pilih TPS" /></SelectTrigger>
                <SelectContent>
                  {tpsList.map((t) => <SelectItem key={t.id} value={t.kode}>{t.kode} — {t.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Disabilitas">
              <Select value={String(form.disabilitas ?? 0)} onValueChange={(v) => update("disabilitas", Number(v))}>
                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DISABILITAS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status || "ACTIVE"} onValueChange={(v) => update("status", v)}>
                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_LIST.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Keterangan" className="md:col-span-2"><Input value={form.keterangan || ""} onChange={(e) => update("keterangan", e.target.value)} className="h-11" /></Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}><X className="h-4 w-4 mr-1" /> Batal</Button>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="voter-form-submit">
              {voter ? "Simpan Perubahan" : "Tambah"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, required, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs font-semibold">{label}{required && <span className="text-red-600"> *</span>}</Label>
      {children}
    </div>
  );
}
